/**
 * i18n — minimal, dependency-free.
 *
 * Mevcut tüm label'lar Türkçe yazılı (default). Switcher EN'e geçtiğinde
 * lookup mapping üzerinden çevirir. Çevrilmemiş key → TR kalır (graceful).
 *
 * Kullanım:
 *   const { t, lang, setLang } = useI18n();
 *   <Text>{t("dashboard.welcome", "Hoş geldin")}</Text>
 */
import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from "react";

export type Locale = "tr" | "en" | "ar" | "ru";

const TRANSLATIONS: Record<Locale, Record<string, string>> = {
  tr: {},
  en: {
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.search": "Search",
    "common.loading": "Loading...",
    "common.empty": "No data",
    "common.actions": "Actions",
    "common.confirm": "Confirm",
    "common.yes": "Yes",
    "common.no": "No",
    "nav.dashboard": "Dashboard",
    "nav.orders": "Orders",
    "nav.tables": "Tables",
    "nav.floor_plan": "Floor Plan",
    "nav.reservations": "Reservations",
    "nav.categories": "Categories",
    "nav.products": "Products",
    "nav.stock": "Stock",
    "nav.stock_alerts": "Stock Alerts",
    "nav.daily_cashier": "Daily Report (Z)",
    "nav.product_sales": "Product Sales",
    "nav.customers": "Customers",
    "nav.loyalty": "Loyalty",
    "nav.personnel": "Personnel",
    "nav.suppliers": "Suppliers",
    "nav.po": "Purchase Orders",
    "nav.gr": "Goods Receipts",
    "nav.supplier_invoices": "Supplier Invoices",
    "nav.expenses": "Expenses",
    "nav.invoices": "E-Invoices",
    "nav.billing": "Billing",
    "nav.delivery": "Delivery",
    "nav.notifications": "Notifications",
    "nav.users": "Users & RBAC",
    "nav.audit": "Audit Log",
    "nav.settings": "Settings",
    "dashboard.welcome": "Welcome",
    "dashboard.today_revenue": "Today's Revenue",
    "dashboard.orders": "Order Count",
    "dashboard.net_profit": "Net Profit",
    "dashboard.open_orders": "Open Orders",
    "pos.tables": "Tables",
    "pos.order": "Order",
    "pos.payment": "Payment",
    "pos.qr_approval": "QR Approval",
    "pos.profile": "Profile",
    "pos.cart": "Cart",
    "pos.total": "Total",
    "pos.cash": "Cash",
    "pos.card": "Card",
    "pos.qr": "QR / Digital",
    "table.free": "Free",
    "table.occupied": "Occupied",
    "table.reserved": "Reserved",
    "table.cleaning": "Cleaning",
    "auth.login": "Login",
    "auth.logout": "Logout",
    "auth.username": "Username",
    "auth.password": "Password",
    // --- QR menü (müşteriye dönük) ---
    "qr.open_menu": "Open Menu",
    "qr.reservation": "Make a Reservation",
    "qr.landing_hint": "Browse the menu and order from your phone",
    "qr.invalid": "Invalid QR",
    "qr.invalid_desc": "This QR code is invalid or expired.",
    "qr.loading": "Loading menu...",
    "qr.ingredients": "Ingredients",
    "qr.allergen_warning": "Allergen Warning",
    "qr.extras": "Extra Options",
    "qr.calories": "kcal",
    "qr.add_to_cart": "Add to Cart",
    "qr.cart": "Cart",
    "qr.cart_title": "My Cart",
    "qr.cart_empty": "Your cart is empty",
    "qr.cart_button": "Cart",
    "qr.total": "Total",
    "qr.place_order": "Place Order",
    "qr.place_order_full": "Place Order (Waiter Approval)",
    "qr.my_orders": "My Orders",
    "qr.back_to_menu": "Back to Menu",
    "qr.request_bill": "Request Bill",
    "qr.call_waiter": "Call Waiter",
    "qr.notes": "Notes",
    "qr.name": "Name",
    "qr.phone": "Phone",
    "qr.cancel": "Cancel",
    "qr.close": "Close",
    "qr.login": "Login",
    "qr.account": "Account",
    "qr.back": "Back",
    "qr.table": "Table",
    "qr.menu": "Menu",
    "qr.search": "Search menu...",
    "qr.remove_from_cart": "Remove from cart",
    "qr.select_category": "Select category",
    "qr.select_category_of": "{cat} — select category",
    "qr.items_count": "{n} items",
    "qr.added_to_cart": "{name} added to cart",
    "qr.no_products_in_category": "No products in this category",
    "qr.no_subgroups": "No subgroups in this category",
    "qr.menu_not_ready": "Menu is not ready yet",
    "qr.add_to_cart_price": "Add to Cart",
    // Sipariş takip
    "qr.cancelled": "Cancelled",
    "qr.no_orders_yet": "No orders yet",
    "qr.no_orders_hint": "Pick items from the menu to place your order.",
    "qr.bill_no": "Bill",
    // Ödeme
    "qr.payment_title": "Bill / Payment",
    "qr.total_amount": "Total Amount",
    "qr.payment_method": "Payment method",
    "qr.pay_single": "Single Payment",
    "qr.pay_split": "Split Payment",
    "qr.split_question": "How many ways to split?",
    "qr.people": "people",
    "qr.per_person": "Approx. per person",
    "qr.payment_note": "On confirmation, a waiter is called. Payment is collected at the table.",
    "qr.status_received": "Received",
    "qr.status_approved": "Approved",
    "qr.status_preparing": "Preparing",
    "qr.status_ready": "Ready",
    "qr.status_served": "Served",
  },
  ar: {
    "common.save": "حفظ",
    "common.cancel": "إلغاء",
    "common.search": "بحث",
    "nav.dashboard": "لوحة التحكم",
    "nav.orders": "الطلبات",
    "nav.tables": "الطاولات",
    "nav.products": "المنتجات",
    "nav.customers": "العملاء",
    "pos.total": "المجموع",
    "pos.cash": "نقدا",
    "pos.card": "بطاقة",
  },
  ru: {
    "common.save": "Сохранить",
    "common.cancel": "Отмена",
    "common.search": "Поиск",
    "nav.dashboard": "Панель",
    "nav.orders": "Заказы",
    "nav.tables": "Столы",
    "nav.products": "Товары",
    "nav.customers": "Клиенты",
    "pos.total": "Итого",
    "pos.cash": "Наличные",
    "pos.card": "Карта",
  },
};

interface I18nContextValue {
  lang: Locale;
  setLang: (l: Locale) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Locale>(() => {
    const stored = (typeof window !== "undefined" && localStorage.getItem("locale")) as Locale | null;
    return stored || "tr";
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }
  }, [lang]);

  function setLang(l: Locale) {
    setLangState(l);
    if (typeof window !== "undefined") {
      localStorage.setItem("locale", l);
    }
  }

  function t(key: string, fallback?: string): string {
    return TRANSLATIONS[lang]?.[key] || fallback || key;
  }

  return createElement(I18nContext.Provider, { value: { lang, setLang, t } }, children);
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback (provider yoksa): TR + key-as-text
    return {
      lang: "tr" as Locale,
      setLang: () => {},
      t: (_k: string, fallback?: string) => fallback || _k,
    };
  }
  return ctx;
}
